import { computed, markRaw, shallowRef, type Component } from 'vue'

type ModalSize = 'default' | 'large' | 'tall'
type ModalProps = Record<string, unknown>

interface ModalOptions {
  closable?: boolean
  size?: ModalSize
  onClose?: () => void
}

interface ModalEntry {
  id: symbol
  title: string
  component: Component
  props: ModalProps
  closable: boolean
  size: ModalSize
  onClose?: () => void
}

const modalStack = shallowRef<ModalEntry[]>([])
const currentModal = computed(() => modalStack.value[modalStack.value.length - 1])

export const useModal = () => {
  const openModal = (
    modalTitle: string,
    modalComponent: Component,
    modalProps: ModalProps = {},
    options: ModalOptions = {},
  ) => {
    modalStack.value = [
      ...modalStack.value,
      {
        id: Symbol(modalTitle),
        title: modalTitle,
        component: markRaw(modalComponent),
        props: modalProps,
        closable: options.closable ?? true,
        size: options.size ?? 'default',
        onClose: options.onClose,
      },
    ]
  }

  const closeModal = () => {
    const modal = currentModal.value
    if (!modal) return

    modalStack.value = modalStack.value.slice(0, -1)
    modal.onClose?.()
  }

  const closeAllModals = () => {
    while (currentModal.value) {
      closeModal()
    }
  }

  return {
    modalStack,
    isModalOpen: computed(() => modalStack.value.length > 0),
    isModalClosable: computed(() => currentModal.value?.closable ?? true),
    title: computed(() => currentModal.value?.title),
    component: computed(() => currentModal.value?.component),
    props: computed(() => currentModal.value?.props ?? {}),
    size: computed(() => currentModal.value?.size ?? 'default'),
    openModal,
    closeModal,
    closeAllModals,
  }
}
